import { Button } from "@/components/ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCopyToClipboard } from "@/hooks";
import { useToast } from "@/hooks/use-toast";
import { Copy, FileText } from "lucide-react";
import type { AttachmentFile } from "../../../../state/stores/types";
import { Markdown } from "../../Markdown";
import { formatDate, formatFileSize } from "./utils";

interface AttachmentData {
  id: string;
  name: string;
  content: string;
  size: number;
  lastModified: number;
}

interface UserAttachmentPreviewModalProps {
  attachment: AttachmentFile | AttachmentData | null;
  isOpen: boolean;
  onClose: () => void;
}

export function UserAttachmentPreviewModal({
  attachment,
  isOpen,
  onClose,
}: UserAttachmentPreviewModalProps) {
  const { toast } = useToast();
  const { isCopied, copyToClipboard } = useCopyToClipboard();

  if (!attachment) {
    return null;
  }

  const handleCopyContent = () => {
    copyToClipboard(attachment.content);
    toast({
      title: "Content copied to clipboard",
    });
  };

  const isMarkdown = attachment.name.endsWith(".md");

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <span className="truncate">{attachment.name}</span>
          </DialogTitle>
          <p className="text-sm text-muted-foreground text-left">
            {formatFileSize(attachment.size)} &bull; Modified{" "}
            {formatDate(attachment.lastModified)}
          </p>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 min-h-0 border rounded-md p-4 bg-muted/50 overflow-auto">
            {isMarkdown ? (
              <Markdown className="max-w-none">{attachment.content}</Markdown>
            ) : (
              <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed">
                {attachment.content}
              </pre>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4 pt-4 border-t flex-shrink-0">
            <Button
              variant="outline"
              onClick={handleCopyContent}
              className="flex items-center gap-2"
            >
              <Copy className="h-4 w-4" />
              {isCopied ? "Copied!" : "Copy Content"}
            </Button>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
